<template>
  <section class="t-left">
    <!-- Кнопка «Назад» над заголовком (конвенция проекта — button--card button--m):
         на шаге > 1 — к предыдущему шагу, иначе на предыдущую страницу. -->
    <button class="button button--card button--m d-inline-flex flex-v-center gap-small mn-b-medium align-self-start" @click="goBack">
      <svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 512 512" height="1em" width="1em"><path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l128 128c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 288 480 288c17.7 0 32-14.3 32-32s-14.3-32-32-32l-370.7 0 73.4-73.4c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-128 128z"></path></svg>
      {{ t('auth.signUp.back') }}
    </button>

    <h3 class="mn-b-medium">{{ t('auth.resetPassword.forgotPasswordTitle') }}</h3>

    <!-- ── Шаг 1: идентификатор (email / телефон) ───────────────────── -->
    <template v-if="step === 'identifier'">
      <p class="p-small mn-b-medium opacity-50">{{ t('auth.resetPassword.instructions') }}</p>

      <!-- Tab selector -->
      <div v-if="availableTabs.length > 1" class="mn-b-small">
        <Tab v-model:selected="tabAuth" :tabs="availableTabs" classTabBar="w-100" />
      </div>

      <div class="pos-relative">
        <transition name="slide-fade">
          <div v-show="tabAuth === 'phone'" class="mn-b-small">
            <!-- Телефон и почта пишут в один identifier, поэтому имя для тестов
                 задано руками: иначе оба поля получат одно имя. -->
            <FieldPhone
              data-testid="field:phone"
              @change="(event) => identifier = event"
              :dropdownOptions="{ showDialCodeInSelection: true, showFlags: true, showDialCodeInList: true }"
              :validation="phoneValidation"
              mode="national"
              :inputOptions="{ placeholder: t('auth.resetPassword.phonePlaceholder') }"
              class="input mn-b-thin"
            />
          </div>
        </transition>
        <transition name="slide-fade">
          <div v-show="tabAuth === 'email'" class="mn-b-small">
            <Field
              v-model:field="identifier"
              :placeholder="t('auth.resetPassword.emailPlaceholder')"
              :validation="emailValidation"
              class="input"
            />
          </div>
        </transition>
      </div>

      <Button :submit="onSendCode" class="w-100 button--accent">
        {{ t('auth.resetPassword.sendCode') }}
      </Button>
    </template>

    <!-- ── Шаг 2: код из письма / SMS ───────────────────────────────── -->
    <template v-else-if="step === 'code'">
      <EnterCode
        v-model="code"
        :identifier="identifier"
        :is-email="tabAuth === 'email'"
        :submit="onVerifyCode"
        :resend="onResend"
      />
    </template>

    <!-- ── Шаг 3: новый пароль ──────────────────────────────────────── -->
    <template v-else>
      <p class="p-small mn-b-medium opacity-50">{{ t('auth.enterPassword.newPasswordPrompt') }}</p>

      <div class="mn-b-small">
        <Field
          v-model:field="password"
          type="password"
          :placeholder="t('auth.enterPassword.passwordPlaceholder')"
          :validation="passwordValidation"
          class="input mn-b-thin"
        />
        <Field
          v-model:field="passwordRepeat"
          type="password"
          :placeholder="t('auth.enterPassword.repeatPasswordPlaceholder')"
          class="input"
        />
      </div>

      <Button :submit="onSubmit" class="w-100 button--accent mn-b-small">
        {{ t('auth.enterPassword.changePasswordBtn') }}
      </Button>
    </template>

    <!-- Ссылка на вход — прибита к низу карточки -->
    <div class="w-100 mn-t-auto pd-t-medium">
      <router-link
        :to="{ name: 'Sign In', query: { returnUrl: route.query.returnUrl } }"
        class="underline d-block t-blue"
      >
        {{ t('auth.signUp.haveAccount') }}
      </router-link>
    </div>
  </section>
</template>

<script setup>
import Tab from '@martyrs/src/design-system/components/Tab/Tab.vue'
import Field from '@martyrs/src/design-system/components/Field/Field.vue'
import FieldPhone from '@martyrs/src/design-system/components/FieldPhone/FieldPhone.vue'
import Button from '@martyrs/src/design-system/components/Button/Button.vue'
import EnterCode from '@martyrs/src/modules/auth/views/components/blocks/EnterCode.vue'

import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

import { useStore } from '@martyrs/src/modules/core/views/store/core.store.js'
import { setError } from '@martyrs/src/modules/core/views/store/core.store.js'
import * as auth from '@martyrs/src/modules/auth/views/store/auth.js'
import $axios from '@martyrs/src/modules/core/views/utils/axios-instance.js'
import * as inputsValidation from '@martyrs/src/modules/auth/views/validations/inputs.validation'

const store = useStore()
const core = store.core || { state: {} }

const route = useRoute()
const router = useRouter()
const { t } = useI18n({ useScope: 'global' })

// Мастер сброса: идентификатор → код → новый пароль. Как и в регистрации, пароль
// вводится после подтверждения кода. Сервер /api/auth/reset-password уже это
// поддерживает: первый запрос без пароля доходит до stepUp('reset-password') и
// шлёт код, пароль нужен только на финальном updatePassword — флоу клиентский.
const steps = ['identifier', 'code', 'password']
const step = ref('identifier')

const identifier = ref('')
const code = ref('')
const password = ref('')
const passwordRepeat = ref('')
const challengeId = ref(null)

const phoneValidation = ref(null)
const emailValidation = ref(null)
const codeValidation = ref(null)
const passwordValidation = ref(null)

const availableTabs = computed(() => {
  const tabs = [
    { name: t('auth.resetPassword.phone'), value: 'phone' },
    { name: t('auth.resetPassword.email'), value: 'email' }
  ]
  const excludeMethods = core.state?.options?.auth?.authMethodsExclude || []
  return tabs.filter(tab => !excludeMethods.includes(tab.value))
})

const tabAuth = ref(availableTabs.value.length ? availableTabs.value[0].value : '')

async function validateIdentifier() {
  if (tabAuth.value === 'phone') {
    await inputsValidation.validateInputs(
      phoneValidation, inputsValidation.validatePhone, identifier.value, 'Некорректный телефон'
    )
  } else {
    await inputsValidation.validateInputs(
      emailValidation, inputsValidation.validateEmail, identifier.value, 'Некорректный email'
    )
  }
}

// Шаг 1 → отправка кода. Первый запрос без пароля: сервер проверяет юзера и шлёт OTP.
// silent — не показывать тост самим: повторную отправку разбирает ResendCode
// (пауза между отправками для него не ошибка, а таймер).
async function onSendCode({ silent = false } = {}) {
  await validateIdentifier()
  try {
    const result = await $axios.post('/api/auth/reset-password', {
      identifier: identifier.value,
      type: tabAuth.value
    })
    if (result.data?.status === 'requires_otp' && result.data.challenge?.challengeId) {
      challengeId.value = result.data.challenge.challengeId
      code.value = ''
      step.value = 'code'
    } else {
      throw new Error('OTP_NOT_REQUESTED')
    }
  } catch (error) {
    if (!silent) setError(error)
    throw error
  }
}

// Повторная отправка кода. Ошибку отдаём наверх: ResendCode сам решит, таймер
// это или тост.
function onResend() {
  return onSendCode({ silent: true })
}

// Шаг 2 → проверяем, что код введён, и переходим к паролю. Верификация — на сервере
// при финальной смене пароля (шаг 3).
async function onVerifyCode() {
  await inputsValidation.validateInputs(
    codeValidation,
    (v) => ({ status: typeof v === 'string' && v.trim().length >= 4, message: 'Введите код' }),
    code.value,
    'Введите код'
  )
  step.value = 'password'
}

// Шаг 3 → смена пароля: код + новый пароль уходят вместе. Неверный код возвращает
// на шаг ввода кода.
async function onSubmit() {
  await inputsValidation.validateInputs(
    passwordValidation, inputsValidation.validatePassword, password.value, 'Некорректный пароль'
  )
  if (password.value !== passwordRepeat.value) {
    setError({ response: { data: { errorCode: 'PASSWORDS_DO_NOT_MATCH' } } })
    throw new Error('PASSWORDS_DO_NOT_MATCH')
  }

  try {
    const result = await $axios.post('/api/auth/reset-password', {
      identifier: identifier.value,
      type: tabAuth.value,
      password: password.value,
      challengeId: challengeId.value,
      otp: code.value.trim()
    })

    if (result.data?.accessToken) {
      const Cookies = (await import('js-cookie')).default
      Cookies.set('user', JSON.stringify(result.data), { path: '/', expires: 30 })
      await auth.actions.initialize()
      redirectTo(result.data._id)
    } else {
      router.push({ name: 'Sign In' })
    }
  } catch (error) {
    setError(error)
    const codeErrors = ['INVALID_CODE', 'CODE_NOT_FOUND', 'ALREADY_USED']
    if (codeErrors.includes(error?.response?.data?.errorCode)) {
      step.value = 'code'
    }
    throw error
  }
}

// Куда вести после успеха: returnUrl > конфиг проекта (options.auth.redirectAfterAuth)
// > профиль пользователя (дефолт martyrs).
function redirectTo(userId) {
  if (route.query?.returnUrl) {
    const { returnUrl, ...extraQuery } = route.query
    router.push({ path: returnUrl, query: { ...extraQuery, afterAuth: 'true' } })
    return
  }
  const configured = core.state?.options?.auth?.redirectAfterAuth
  if (configured) {
    router.push({ path: configured, query: { afterAuth: 'true' } })
    return
  }
  router.push({ name: 'User Profile', params: { _id: userId }, query: { afterAuth: 'true' } })
}

// «Назад»: на шаге > 1 — предыдущий шаг, на первом — на предыдущую страницу.
function goBack() {
  const i = steps.indexOf(step.value)
  if (i > 0) step.value = steps[i - 1]
  else router.back()
}
</script>

<style lang="scss">
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  position: absolute;
  transform: translateX(20px);
  opacity: 0;
  left: 0;
  top: 0;
}
</style>
